import { useCallback, useEffect, useRef, useState } from "react";
import ExcelJS from "exceljs";
import { useUrlSearchParam } from "../hooks/useUrlSearchParam";
import {
    Plus, RefreshCw, ChevronLeft, ChevronRight,
    Download, Upload, FileText, AlertCircle, CheckCircle,
} from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import FilterPills from "./admin/FilterPills";
import MissingSuppliersModal from "./admin/MissingSuppliersModal";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";
import { useWarehouseScope } from "../access/useWarehouseScope";
import { preflightSuppliers, buildSupplierFixupRows } from "../lib/preflightSuppliers";

const PAGE_SIZE = 25;
const MANIFEST_BULK_CHUNK = 500;
// Manifest XLSX columns. The product columns (title → ship_in_packaging)
// are the same set as the products bulk upload — including them here
// lets a single manifest upload stand up brand-new SKUs and refresh
// existing product specs in one shot. manifest_date sits before
// expected_at so the row reads left-to-right in chronological order:
// PO issued → expected delivery.
// `rrp` sits directly after `unit_cost` so the manifest cost and the
// retail price the product sells at read side-by-side. It's a product
// field (flows to products.rrp via the BE upsert); the manifests table
// itself doesn't store it.
const MANIFEST_HEADER = [
    "action", "manifest_id",
    "sku", "warehouse", "expected_qty", "received_qty",
    "unit_cost", "rrp", "currency",
    "po_number", "supplier_name", "manifest_date", "expected_at", "notes",
    "title", "barcode", "image_url",
    "weight_grams", "length_cm", "width_cm", "height_cm",
    "country_of_origin", "commodity_code", "ship_in_packaging",
];

// Identifier-style columns forced to Text format on export so Excel
// preserves long digit strings and HS commodity codes verbatim. Numeric
// columns are deliberately left alone so the operator can still sort /
// filter on quantities and costs.
const TEXT_COLUMNS = new Set<string>([
    "manifest_id", "sku", "barcode", "commodity_code", "po_number",
]);

const cellToString = (cell: ExcelJS.Cell | undefined): string => {
    if (!cell) return "";
    const v = cell.value;
    if (v === null || v === undefined) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === "object") {
        if ("result" in v && (v as { result?: unknown }).result != null) {
            return String((v as { result: unknown }).result);
        }
        if ("text" in v && (v as { text?: unknown }).text != null) {
            return String((v as { text: unknown }).text);
        }
        if ("richText" in v) {
            const rt = (v as { richText?: { text: string }[] }).richText;
            return Array.isArray(rt) ? rt.map(r => r.text).join("") : "";
        }
    }
    return String(v);
};

const downloadXlsx = async (filename: string, header: readonly string[], rows: string[][]) => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Manifests");
    ws.addRow(header as string[]);
    for (const r of rows) ws.addRow(r);
    header.forEach((name, i) => {
        if (TEXT_COLUMNS.has(name)) ws.getColumn(i + 1).numFmt = "@";
    });
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const readXlsxRows = async (file: File): Promise<{ header: string[]; rows: string[][] }> => {
    const buffer = await file.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buffer);
    const ws = wb.worksheets[0];
    if (!ws || ws.rowCount < 1) return { header: [], rows: [] };
    const headerRow = ws.getRow(1);
    const colCount = headerRow.cellCount;
    const header: string[] = [];
    for (let c = 1; c <= colCount; c++) {
        header[c - 1] = cellToString(headerRow.getCell(c)).trim().toLowerCase();
    }
    const rows: string[][] = [];
    for (let r = 2; r <= ws.rowCount; r++) {
        const xrow = ws.getRow(r);
        const cells: string[] = [];
        for (let c = 1; c <= colCount; c++) cells[c - 1] = cellToString(xrow.getCell(c));
        if (cells.some(s => s.trim() !== "")) rows.push(cells);
    }
    return { header, rows };
};

interface BulkResult {
    added: number;
    updated: number;
    removed: number;
    errors: { row: number; reason: string }[];
}

interface ManifestRow {
    manifest_id: number;
    sku: string;
    warehouse: string;
    expected_qty: number;
    remaining_qty: number;
    // Monotonic counter — grows on every book-in match, including
    // overages. May exceed expected_qty; the table renders the diff
    // as a "+N" badge when that happens.
    received_qty: number;
    unit_cost: number;
    currency: string;
    po_number?: string | null;
    supplier_name?: string | null;
    manifest_date?: string | null;
    expected_at?: string | null;
    notes?: string | null;
    status: "open" | "fulfilled" | "cancelled" | "archived";
    created_by_uid?: string | null;
    created_by_name?: string | null;
    created_at: string;
    updated_at: string;
}

const formatMoney = (value: number, currency = "GBP") => {
    try { return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value); }
    catch { return `${currency} ${value.toFixed(2)}`; }
};

const formatDate = (iso: string) => {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString();
    } catch { return iso; }
};

// Manifests admin: lists expected-stock entries (POs / preorders) and
// lets admins create / cancel / archive them. Book-in matches scans
// against open manifest rows at completion time and pulls cost from
// the matched row.
const ManifestsAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [manifests, setManifests] = useState<ManifestRow[]>([]);
    const [total, setTotal] = useState(0);
    const warehouseScope = useWarehouseScope();
    const [allWarehouses, setWarehouses] = useState<string[]>([]);
    const warehouses = warehouseScope.limit(allWarehouses);
    const [pickedWarehouse, setWarehouseFilter] = useState<string>("");
    // Without cross_warehouse the list is pinned to the person's own warehouse.
    const warehouseFilter = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    // Free-text search — BE matches across sku, po_number, supplier_name.
    // Commits on Enter / blur so a fast typist doesn't hit the BE per
    // keystroke. `?search=` URL param lets AdminSearch deep-link in
    // pre-filtered.
    const [searchFilter, setSearchFilter] = useUrlSearchParam("search");
    const [searchInput, setSearchInput] = useState(searchFilter);
    useEffect(() => { setSearchInput(searchFilter); }, [searchFilter]);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);

    const [submittingManifest, setSubmittingManifest] = useState(false);
    const [manifestUploading, setManifestUploading] = useState(false);
    const [manifestUploadProgress, setManifestUploadProgress] = useState<{ done: number; total: number } | null>(null);
    const [manifestUploadResult, setManifestUploadResult] = useState<BulkResult | null>(null);
    const manifestFileInputRef = useRef<HTMLInputElement>(null);
    const [missingSuppliers, setMissingSuppliers] = useState<{
        missing: string[];
        archived: string[];
        resolve: (confirmed: boolean) => void;
    } | null>(null);
    const [manifestForm, setManifestForm] = useState({
        sku: "",
        warehouse: "",
        expected_qty: "",
        unit_cost: "",
        // Product RRP — flows to products.rrp via the BE upsert, not
        // stored on the manifest row. Sits next to unit_cost in the form.
        rrp: "",
        currency: "GBP",
        po_number: "",
        supplier_name: "",
        manifest_date: "",
        expected_at: "",
        notes: "",
        // Product fields — same set the CSV upload understands. Blank
        // values are sent to the BE as null so an existing product's
        // spec sheet isn't overwritten by an empty inline-add row.
        title: "",
        barcode: "",
        image_url: "",
        weight_grams: "",
        length_cm: "",
        width_cm: "",
        height_cm: "",
        country_of_origin: "",
        commodity_code: "",
        ship_in_packaging: false,
    });

    useEffect(() => { setPage(0); }, [warehouseFilter, searchFilter]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (warehouseFilter)    params.set("warehouse", warehouseFilter);
            if (searchFilter.trim()) params.set("q", searchFilter.trim());
            // Show every non-archived manifest so admins can see fulfilled
            // / cancelled rows, not just the still-open ones.
            params.set("status", "all");
            params.set("page", String(page));
            params.set("page_size", String(PAGE_SIZE));
            const res = await apiFetch(`/manifest?${params}`, {});
            if (res.ok) {
                const data = await res.json();
                setManifests(Array.isArray(data.manifests) ? data.manifests : []);
                setTotal(typeof data.total === "number" ? data.total : 0);
            }
        } catch {
            toast.error(t("server_error"), { toastId: "manifest-load-err" });
        } finally {
            setLoading(false);
        }
    }, [warehouseFilter, searchFilter, page, t]);

    // Load warehouses once for the filter pills + form select.
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const data: { warehouse: string }[] = await res.json();
                setWarehouses(data.map(r => r.warehouse).sort());
            } catch { /* leave empty */ }
        })();
    }, []);

    useEffect(() => { refresh(); }, [refresh]);


    const submitManifest = async () => {
        const f = manifestForm;
        if (!f.sku.trim() || !f.warehouse) {
            toast.error(t("manifest_invalid"), { toastId: "manifest-err" });
            return;
        }
        const qty = Number(f.expected_qty);
        const cost = Number(f.unit_cost);
        if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(cost) || cost < 0) {
            toast.error(t("manifest_invalid"), { toastId: "manifest-err" });
            return;
        }
        setSubmittingManifest(true);
        try {
            let createdByUid = "";
            let createdByName = "";
            try {
                const userData = window.localStorage.getItem("user");
                if (userData) {
                    const u = JSON.parse(userData);
                    createdByUid = u?.uid ?? "";
                    createdByName = u?.name ?? "";
                }
            } catch { /* fall through */ }

            // Numeric product fields: blank → null so the BE's
            // upsert leaves an existing product's value alone.
            const numOrNullStr = (v: string) => {
                const trimmed = v.trim();
                if (trimmed === "") return null;
                const n = Number(trimmed);
                return Number.isFinite(n) && n >= 0 ? n : null;
            };
            const trimOrNull = (v: string) => {
                const s = v.trim();
                return s === "" ? null : s;
            };

            const res = await apiFetch(`/manifest`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    sku: f.sku.trim(),
                    warehouse: f.warehouse,
                    expected_qty: Math.round(qty),
                    unit_cost: cost,
                    rrp: numOrNullStr(f.rrp),
                    currency: f.currency || "GBP",
                    po_number: f.po_number.trim() || null,
                    supplier_name: f.supplier_name.trim() || null,
                    manifest_date: f.manifest_date || null,
                    expected_at: f.expected_at || null,
                    notes: f.notes.trim() || null,
                    // Product fields — BE upserts the products row
                    // alongside the manifest insert.
                    title:             trimOrNull(f.title),
                    barcode:           trimOrNull(f.barcode),
                    image_url:         trimOrNull(f.image_url),
                    weight_grams:      numOrNullStr(f.weight_grams),
                    length_cm:         numOrNullStr(f.length_cm),
                    width_cm:          numOrNullStr(f.width_cm),
                    height_cm:         numOrNullStr(f.height_cm),
                    country_of_origin: trimOrNull(f.country_of_origin),
                    commodity_code:    trimOrNull(f.commodity_code),
                    ship_in_packaging: f.ship_in_packaging,
                    created_by_uid: createdByUid,
                    created_by_name: createdByName,
                }),
            });
            if (!res.ok) throw new Error("manifest");
            toast.success(t("saved"));
            setManifestForm({
                sku: "", warehouse: f.warehouse, expected_qty: "",
                unit_cost: "", rrp: "", currency: "GBP", po_number: "", supplier_name: "",
                manifest_date: "", expected_at: "", notes: "",
                title: "", barcode: "", image_url: "",
                weight_grams: "", length_cm: "", width_cm: "", height_cm: "",
                country_of_origin: "", commodity_code: "", ship_in_packaging: false,
            });
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "manifest-server-err" });
        } finally {
            setSubmittingManifest(false);
        }
    };

    const downloadManifestTemplate = async () => {
        // Row layout matches MANIFEST_HEADER. Product columns (title …
        // ship_in_packaging) are populated on the first occurrence of
        // each SKU so a brand-new SKU's product record gets created;
        // re-using the same SKU on later rows can leave them blank to
        // skip the product refresh, or repeat them to update specs.
        const rows: string[][] = [
            // PO-2026-001: three SKUs, all bound for Newport. manifest_id
            // and received_qty are blank on add — the BE assigns the
            // BIGSERIAL id and tracks received_qty as book-ins land. rrp
            // sits between unit_cost and currency and flows to the product.
            ["add", "", "JLI-WH-00482", "Newport", "100", "", "1850.00", "2499.99", "GBP", "PO-2026-001", "Dyson Limited", "2026-05-01", "2026-05-15", "Multi-SKU PO: 1 of 3 — vacuum cleaners",
             "Dyson V15 Cordless Vacuum", "5025155067666", "https://example.com/img/v15.jpg",
             "3500", "32", "25", "126", "Malaysia", "8508110000", "false"],
            ["add", "", "JLI-SM-00044", "Newport",  "50",   "", "95.00", "149.99", "GBP", "PO-2026-001", "Dyson Limited", "2026-05-01", "2026-05-15", "Multi-SKU PO: 2 of 3 — accessory pack",
             "Dyson Accessory Pack", "5025155088888", "",
             "1200", "20", "15", "10", "Malaysia", "8508700000", "false"],
            ["add", "", "JLI-EL-00891", "Newport",  "20",   "", "38.00", "59.99", "GBP", "PO-2026-001", "Dyson Limited", "2026-05-01", "2026-05-15", "Multi-SKU PO: 3 of 3 — spare filter",
             "Dyson Replacement Filter", "5025155099999", "",
             "200", "12", "12", "5", "Malaysia", "8421390000", "true"],
            // Separate PO, different warehouse. Product cells left blank
            // (including rrp) because JLI-SM-00044's spec sheet was already
            // populated by the row above — the BE skips the product refresh
            // on a re-upload that doesn't change anything.
            ["add", "", "JLI-SM-00044", "Poland",   "50",   "", "95.00", "", "GBP", "PO-2026-002", "Samsung Electronics", "2026-05-01", "", "Single-SKU PO",
             "", "", "", "", "", "", "", "", "", ""],
            // cancel/archive only need action + manifest_id (taken from
            // a Download-Current export). Other columns can be blank.
            ["cancel",  "12345", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""],
            ["archive", "67890", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""],
        ];
        await downloadXlsx("manifests-template.xlsx", MANIFEST_HEADER, rows);
    };

    const downloadManifestsCurrent = async () => {
        // Page through the BE so the download covers every manifest matching
        // the current filters, not just the visible 25-row page slice.
        // BE caps page_size at 200 (manifest controller listManifests).
        const DOWNLOAD_PAGE_SIZE = 200;
        const all: ManifestRow[] = [];
        try {
            const baseParams = new URLSearchParams();
            if (warehouseFilter)     baseParams.set("warehouse", warehouseFilter);
            if (searchFilter.trim()) baseParams.set("q", searchFilter.trim());
            baseParams.set("status", "all");
            baseParams.set("page_size", String(DOWNLOAD_PAGE_SIZE));

            let pageNum = 0;
            while (true) {
                const params = new URLSearchParams(baseParams);
                params.set("page", String(pageNum));
                const res = await apiFetch(`/manifest?${params}`, {});
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const list: ManifestRow[] = Array.isArray(data.manifests) ? data.manifests : [];
                all.push(...list);
                const grandTotal = typeof data.total === "number" ? data.total : all.length;
                if (list.length === 0 || all.length >= grandTotal) break;
                pageNum += 1;
            }
        } catch {
            toast.error(t("server_error"), { toastId: "manifest-download-err" });
            return;
        }

        const rows: string[][] = [];
        for (const m of all) {
            rows.push([
                "", // action — blank for current export; user fills in for re-upload
                String(m.manifest_id),
                m.sku,
                m.warehouse,
                String(m.expected_qty),
                String(m.received_qty ?? 0),
                String(m.unit_cost),
                // rrp lives on the product, not the manifest row — blank
                // on export so a re-upload doesn't clobber products.rrp.
                "",
                m.currency,
                m.po_number ?? "",
                m.supplier_name ?? "",
                m.manifest_date ? m.manifest_date.slice(0, 10) : "",
                m.expected_at ? m.expected_at.slice(0, 10) : "",
                m.notes ?? "",
                // Product columns aren't denormalised onto manifests
                // (they live on the products table) so the export
                // leaves them blank — admins fill them in only when
                // they want to refresh / seed product info.
                "", "", "", "", "", "", "", "", "", "",
            ]);
        }
        await downloadXlsx("manifests-current.xlsx", MANIFEST_HEADER, rows);
    };

    const onManifestFileChosen = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const file = ev.target.files?.[0];
        if (!file) return;
        ev.target.value = "";

        let parsed: { header: string[]; rows: string[][] };
        try {
            parsed = await readXlsxRows(file);
        } catch {
            toast.error(t("manifest_upload_parse_error"), { toastId: "manifest-upload-parse" });
            return;
        }
        const { header, rows: dataRows } = parsed;
        if (dataRows.length === 0) {
            toast.error(t("manifest_upload_empty"), { toastId: "manifest-upload-empty" });
            return;
        }
        const idx = (name: string) => header.indexOf(name);
        const actionIdx = idx("action");
        const skuIdx = idx("sku");
        const warehouseIdx = idx("warehouse");
        if (skuIdx === -1 && idx("manifest_id") === -1) {
            toast.error(t("manifest_upload_bad_header"), { toastId: "manifest-upload-hdr" });
            return;
        }

        const cell = (r: string[], i: number) => (i >= 0 ? (r[i] ?? "") : "");
        const rowsToSend = dataRows.map(r => ({
            action:            cell(r, actionIdx),
            sku:               cell(r, skuIdx),
            warehouse:         cell(r, warehouseIdx),
            expected_qty:      cell(r, idx("expected_qty")),
            unit_cost:         cell(r, idx("unit_cost")),
            rrp:               cell(r, idx("rrp")),
            currency:          cell(r, idx("currency")),
            po_number:         cell(r, idx("po_number")),
            supplier_name:     cell(r, idx("supplier_name")),
            manifest_date:     cell(r, idx("manifest_date")),
            expected_at:       cell(r, idx("expected_at")),
            notes:             cell(r, idx("notes")),
            // Product fields — picked up by the BE bulk handler to
            // upsert the products row alongside the manifest insert.
            title:             cell(r, idx("title")),
            barcode:           cell(r, idx("barcode")),
            image_url:         cell(r, idx("image_url")),
            weight_grams:      cell(r, idx("weight_grams")),
            length_cm:         cell(r, idx("length_cm")),
            width_cm:          cell(r, idx("width_cm")),
            height_cm:         cell(r, idx("height_cm")),
            country_of_origin: cell(r, idx("country_of_origin")),
            commodity_code:    cell(r, idx("commodity_code")),
            ship_in_packaging: cell(r, idx("ship_in_packaging")),
            manifest_id:       cell(r, idx("manifest_id")),
        }));

        // Supplier preflight: every supplier_name in the file must be an
        // active supplier. If any are missing or archived, prompt the
        // operator. Cancel = bail before any rows hit the BE.
        try {
            const preflight = await preflightSuppliers(rowsToSend);
            if (!preflight.ok) {
                const confirmed = await new Promise<boolean>(resolve => {
                    setMissingSuppliers({ ...preflight, resolve });
                });
                setMissingSuppliers(null);
                if (!confirmed) {
                    toast.info(t("manifest_upload_cancelled_missing_suppliers"), {
                        toastId: "manifest-upload-cancelled",
                    });
                    if (manifestFileInputRef.current) manifestFileInputRef.current.value = "";
                    return;
                }
                const fixupRows = buildSupplierFixupRows(preflight.missing, preflight.archived);
                if (fixupRows.length > 0) {
                    const r = await apiFetch(`/suppliers/bulk`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ rows: fixupRows }),
                    });
                    if (!r.ok) throw new Error(`suppliers fixup failed: ${r.status}`);
                }
            }
        } catch {
            toast.error(t("server_error"), { toastId: "manifest-preflight-err" });
            if (manifestFileInputRef.current) manifestFileInputRef.current.value = "";
            return;
        }

        setManifestUploading(true);
        setManifestUploadProgress({ done: 0, total: rowsToSend.length });
        setManifestUploadResult(null);

        const combined: BulkResult = { added: 0, updated: 0, removed: 0, errors: [] };
        try {
            for (let i = 0; i < rowsToSend.length; i += MANIFEST_BULK_CHUNK) {
                const chunk = rowsToSend.slice(i, i + MANIFEST_BULK_CHUNK);
                const res = await apiFetch(`/manifest/bulk`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ rows: chunk }),
                });
                // 413 = chunk body exceeds the BE's JSON limit. Stop the
                // upload (subsequent chunks will fail the same way) and
                // surface a specific message so the operator knows it's
                // a size issue.
                if (res.status === 413) {
                    toast.error(t("bulk_upload_too_large"), { toastId: "manifest-upload-413" });
                    break;
                }
                // 403 = the access guard refused the chunk — a permissions
                // answer, not a server fault. When a row names a warehouse
                // the uploader can't act in, the guard says which one
                // (detail "warehouse_scope"), so show that instead of the
                // generic server error.
                if (res.status === 403) {
                    const body = await res.json().catch(() => null);
                    toast.error(
                        body?.detail === "warehouse_scope" && typeof body.warehouse === "string"
                            ? t("manifest_upload_forbidden_warehouse", { warehouse: body.warehouse })
                            : t("manifest_upload_forbidden"),
                        { toastId: "manifest-upload-403" },
                    );
                    break;
                }
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const result: BulkResult = await res.json();
                combined.added += result.added;
                combined.updated += result.updated;
                combined.removed += result.removed;
                for (const e of result.errors) {
                    combined.errors.push({ row: e.row + i, reason: e.reason });
                }
                setManifestUploadProgress({
                    done: Math.min(i + chunk.length, rowsToSend.length),
                    total: rowsToSend.length,
                });
            }
            setManifestUploadResult(combined);
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "manifest-upload-err" });
        } finally {
            setManifestUploading(false);
            setManifestUploadProgress(null);
        }
    };

    // BE returns the page slice already filtered + searched. Pagination
    // math reads BE total instead of client-side row counts.
    const visibleMan = manifests;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const safePage = Math.min(page, Math.max(totalPages - 1, 0));
    const rangeFrom = total === 0 ? 0 : safePage * PAGE_SIZE + 1;
    const rangeTo = Math.min((safePage + 1) * PAGE_SIZE, total);

    // Warehouse pill counts dropped — they were derived from the
    // current page (always 0–25) which is meaningless. The pill labels
    // still toggle the BE warehouse filter; counts could come back via
    // a separate /manifest/counts endpoint if needed.
    const warehouseOptions = warehouses.map(w => ({ value: w, label: w }));

    return (
        <div className="inventory-admin">
            <div className="totes-admin-actions">
                <button type="button" className="btn btn-ghost" onClick={downloadManifestTemplate}>
                    <FileText size={16} /> {t("manifest_download_template")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={downloadManifestsCurrent}
                    disabled={loading || total === 0}
                >
                    <Download size={16} /> {t("manifest_download_current")}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => manifestFileInputRef.current?.click()}
                    disabled={manifestUploading || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                >
                    <Upload size={16} /> {manifestUploading ? t("submitting") : t("manifest_upload_xlsx")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={refresh}
                    disabled={loading}
                    style={{ marginLeft: "auto" }}
                >
                    <RefreshCw size={14} /> {t("refresh")}
                </button>
                <input
                    ref={manifestFileInputRef}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{ display: "none" }}
                    onChange={onManifestFileChosen}
                />
            </div>

            {manifestUploadProgress && (
                <div className="totes-admin-progress">
                    <div className="totes-admin-progress-label">
                        {t("totes_upload_progress", {
                            done: String(manifestUploadProgress.done),
                            total: String(manifestUploadProgress.total),
                        })}
                        <span className="totes-admin-progress-pct">
                            {Math.round((manifestUploadProgress.done / Math.max(manifestUploadProgress.total, 1)) * 100)}%
                        </span>
                    </div>
                    <div className="totes-admin-progress-track">
                        <div
                            className="totes-admin-progress-fill"
                            style={{ width: `${(manifestUploadProgress.done / Math.max(manifestUploadProgress.total, 1)) * 100}%` }}
                        />
                    </div>
                </div>
            )}

            {manifestUploadResult && (
                <div className="totes-admin-result">
                    <div className="totes-admin-result-line">
                        <CheckCircle strokeWidth={3} size={16} className="totes-admin-result-ok" />
                        <span>
                            {t("totes_bulk_summary", {
                                added: String(manifestUploadResult.added),
                                updated: String(manifestUploadResult.updated),
                                removed: String(manifestUploadResult.removed),
                            })}
                        </span>
                    </div>
                    {manifestUploadResult.errors.length > 0 && (
                        <ul className="totes-admin-errors">
                            {manifestUploadResult.errors.map((e, i) => (
                                <li key={i}>
                                    <AlertCircle size={14} />
                                    <span>{t("totes_row_label", { row: String(e.row) })}: {e.reason}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            <details className="admin-settings-subsection inventory-admin-receive">
                <summary className="admin-settings-subsection-summary">
                    <span><Plus size={14} style={{ verticalAlign: "middle" }} /> {t("manifest_add_title")}</span>
                </summary>
                <div className="admin-settings-subsection-body">
                    <div className="dispatch-admin-form-grid">
                        <label className="dispatch-admin-form-label">
                            {t("inventory_col_sku")}
                            <input className="dispatch-admin-form-input"
                                   value={manifestForm.sku}
                                   onChange={e => setManifestForm({ ...manifestForm, sku: e.target.value })}
                                   placeholder="JLI-XX-00000" />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("inventory_col_warehouse")}
                            <select className="dispatch-admin-form-input"
                                    value={manifestForm.warehouse}
                                    onChange={e => setManifestForm({ ...manifestForm, warehouse: e.target.value })}>
                                <option value="">—</option>
                                {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                            </select>
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("manifest_col_expected_qty")}
                            <input type="number" inputMode="numeric" min={1}
                                   className="dispatch-admin-form-input"
                                   value={manifestForm.expected_qty}
                                   onChange={e => setManifestForm({ ...manifestForm, expected_qty: e.target.value })} />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("inventory_col_unit_cost")}
                            <input type="number" inputMode="decimal" min={0} step={0.01}
                                   className="dispatch-admin-form-input"
                                   value={manifestForm.unit_cost}
                                   onChange={e => setManifestForm({ ...manifestForm, unit_cost: e.target.value })} />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("products_col_rrp")}
                            <input type="number" inputMode="decimal" min={0} step={0.01}
                                   className="dispatch-admin-form-input"
                                   value={manifestForm.rrp}
                                   onChange={e => setManifestForm({ ...manifestForm, rrp: e.target.value })} />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("inventory_col_currency")}
                            <input className="dispatch-admin-form-input"
                                   value={manifestForm.currency}
                                   onChange={e => setManifestForm({ ...manifestForm, currency: e.target.value })}
                                   placeholder="GBP" />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("inventory_col_po")}
                            <input className="dispatch-admin-form-input"
                                   value={manifestForm.po_number}
                                   onChange={e => setManifestForm({ ...manifestForm, po_number: e.target.value })}
                                   placeholder="PO-2026-XXXX" />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("inventory_col_supplier")}
                            <input className="dispatch-admin-form-input"
                                   value={manifestForm.supplier_name}
                                   onChange={e => setManifestForm({ ...manifestForm, supplier_name: e.target.value })} />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("manifest_col_manifest_date")}
                            <input type="date"
                                   className="dispatch-admin-form-input"
                                   value={manifestForm.manifest_date}
                                   onChange={e => setManifestForm({ ...manifestForm, manifest_date: e.target.value })} />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("manifest_col_expected_at")}
                            <input type="date"
                                   className="dispatch-admin-form-input"
                                   value={manifestForm.expected_at}
                                   onChange={e => setManifestForm({ ...manifestForm, expected_at: e.target.value })} />
                        </label>

                        {/* Product fields — same set the CSV upload understands.
                            Blank values send null to the BE so an existing product's
                            spec sheet isn't overwritten by an empty inline-add row. */}
                        <label className="dispatch-admin-form-label">
                            {t("products_col_title")}
                            <input className="dispatch-admin-form-input"
                                   value={manifestForm.title}
                                   onChange={e => setManifestForm({ ...manifestForm, title: e.target.value })}
                                   placeholder={manifestForm.sku || ""} />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("products_col_barcode")}
                            <input className="dispatch-admin-form-input"
                                   value={manifestForm.barcode}
                                   onChange={e => setManifestForm({ ...manifestForm, barcode: e.target.value })} />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("products_modal_image")}
                            <input className="dispatch-admin-form-input"
                                   value={manifestForm.image_url}
                                   onChange={e => setManifestForm({ ...manifestForm, image_url: e.target.value })}
                                   placeholder="https://…" />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("products_col_weight")}
                            <input type="number" inputMode="numeric" min={0}
                                   className="dispatch-admin-form-input"
                                   value={manifestForm.weight_grams}
                                   onChange={e => setManifestForm({ ...manifestForm, weight_grams: e.target.value })}
                                   placeholder="g" />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("products_modal_length")}
                            <input type="number" inputMode="decimal" min={0} step={0.1}
                                   className="dispatch-admin-form-input"
                                   value={manifestForm.length_cm}
                                   onChange={e => setManifestForm({ ...manifestForm, length_cm: e.target.value })} />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("products_modal_width")}
                            <input type="number" inputMode="decimal" min={0} step={0.1}
                                   className="dispatch-admin-form-input"
                                   value={manifestForm.width_cm}
                                   onChange={e => setManifestForm({ ...manifestForm, width_cm: e.target.value })} />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("products_modal_height")}
                            <input type="number" inputMode="decimal" min={0} step={0.1}
                                   className="dispatch-admin-form-input"
                                   value={manifestForm.height_cm}
                                   onChange={e => setManifestForm({ ...manifestForm, height_cm: e.target.value })} />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("products_col_origin")}
                            <input className="dispatch-admin-form-input"
                                   value={manifestForm.country_of_origin}
                                   onChange={e => setManifestForm({ ...manifestForm, country_of_origin: e.target.value })} />
                        </label>
                        <label className="dispatch-admin-form-label">
                            {t("products_modal_commodity")}
                            <input className="dispatch-admin-form-input"
                                   value={manifestForm.commodity_code}
                                   onChange={e => setManifestForm({ ...manifestForm, commodity_code: e.target.value })} />
                        </label>
                        <label className="dispatch-admin-form-label dispatch-admin-form-label--check">
                            <input type="checkbox"
                                   checked={manifestForm.ship_in_packaging}
                                   onChange={e => setManifestForm({ ...manifestForm, ship_in_packaging: e.target.checked })} />
                            <span>{t("products_modal_ship_in_packaging")}</span>
                        </label>
                    </div>
                    <div className="modal-confirm-buttons" style={{ marginTop: 12 }}>
                        <button className="btn btn-success btn-sm" onClick={submitManifest} disabled={submittingManifest || readOnly} aria-disabled={readOnly} title={readOnly ? t("read_only_mode") : undefined}>
                            <Plus size={14} /> {submittingManifest ? t("submitting") : t("manifest_add_btn")}
                        </button>
                    </div>
                </div>
            </details>

            <FilterPills
                className="warehouse-admin-pills"
                value={warehouseFilter}
                onChange={setWarehouseFilter}
                hideAll={!warehouseScope.canCross}
                options={warehouseOptions}
            />

            <div className="warehouse-admin-filters users-admin-filters">
                <input
                    className="locations-table-search wh-admin-filter-search"
                    type="search"
                    placeholder={t("manifest_filter_search_hint")}
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            setSearchFilter(searchInput.trim());
                        }
                    }}
                    onBlur={() => {
                        if (searchInput.trim() !== searchFilter.trim()) {
                            setSearchFilter(searchInput.trim());
                        }
                    }}
                />
            </div>

            <div className="locations-table-wrap">
                <table className="locations-table">
                    <thead>
                        <tr>
                            <th className="locations-table-num">{t("manifest_col_id")}</th>
                            <th>{t("inventory_col_sku")}</th>
                            <th>{t("inventory_col_warehouse")}</th>
                            <th className="locations-table-num">{t("manifest_col_received")}</th>
                            <th className="locations-table-num">{t("inventory_col_unit_cost")}</th>
                            <th>{t("inventory_col_supplier")}</th>
                            <th>{t("inventory_col_po")}</th>
                            <th>{t("manifest_col_manifest_date")}</th>
                            <th>{t("manifest_col_expected_at")}</th>
                            <th>{t("manifest_col_status")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleMan.map(r => {
                            const overage = r.received_qty - r.expected_qty;
                            return (
                                <tr key={r.manifest_id}>
                                    <td className="locations-table-num locations-table-mono">{r.manifest_id}</td>
                                    <td className="locations-table-mono">{r.sku}</td>
                                    <td>{r.warehouse}</td>
                                    <td className="locations-table-num">
                                        {r.received_qty} / {r.expected_qty}
                                        {overage > 0 && (
                                            <span className="manifest-received-overage">+{overage}</span>
                                        )}
                                    </td>
                                    <td className="locations-table-num">{formatMoney(r.unit_cost, r.currency)}</td>
                                    <td>{r.supplier_name ?? "—"}</td>
                                    <td>{r.po_number ?? "—"}</td>
                                    <td>{r.manifest_date ? formatDate(r.manifest_date) : "—"}</td>
                                    <td>{r.expected_at ? formatDate(r.expected_at) : "—"}</td>
                                    <td>{t(`manifest_status_${r.status}`)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {!loading && total === 0 && (
                <div className="locations-table-empty">{t("manifest_no_rows")}</div>
            )}

            {total > 0 && (
                <div className="locations-table-footer">
                    <span>{t("locations_showing_range", {
                        from: String(rangeFrom),
                        to: String(rangeTo),
                        total: String(total),
                    })}</span>
                    <div className="locations-table-pagination">
                        <button
                            type="button"
                            className="locations-table-page-btn"
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={safePage === 0}
                            aria-label={t("pagination_previous")}
                        >
                            <ChevronLeft size={16} />
                            <span>{t("pagination_previous")}</span>
                        </button>
                        <span className="locations-table-page-indicator">
                            {t("pagination_page_x_of_y", {
                                page: String(safePage + 1),
                                total: String(totalPages),
                            })}
                        </span>
                        <button
                            type="button"
                            className="locations-table-page-btn"
                            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={safePage >= totalPages - 1}
                            aria-label={t("pagination_next")}
                        >
                            <span>{t("pagination_next")}</span>
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}

            {missingSuppliers && (
                <MissingSuppliersModal
                    missing={missingSuppliers.missing}
                    archived={missingSuppliers.archived}
                    onConfirm={() => missingSuppliers.resolve(true)}
                    onCancel={() => missingSuppliers.resolve(false)}
                />
            )}
        </div>
    );
};

export default ManifestsAdmin;
